/**
 * Button component with variants and WCAG AA accessibility
 */
import React from 'react';
import { colors, spacing, typography, borderRadius, transitions, typographyFlat } from '../../styles/tokens';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const variantStyles: Record<ButtonVariant, React.CSSProperties> = {
  primary: {
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
  },
  secondary: {
    backgroundColor: colors.secondary,
    color: colors.white,
    border: 'none',
  },
  outline: {
    backgroundColor: 'transparent',
    color: colors.primary,
    border: `2px solid ${colors.primary}`,
  },
  ghost: {
    backgroundColor: 'transparent',
    color: colors.primary,
    border: 'none',
  },
  danger: {
    backgroundColor: colors.error,
    color: colors.white,
    border: 'none',
  },
};

const sizeStyles: Record<ButtonSize, React.CSSProperties> = {
  sm: {
    padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typography.small.fontSize,
    height: `calc(${spacing.md} * 2)`,
  },
  md: {
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: typography.body.fontSize,
    height: `${spacing.xl}`,
  },
  lg: {
    padding: `${spacing.md} ${spacing.lg}`,
    fontSize: typographyFlat.fontSize.lg,
    height: `calc(${spacing.lg} * 2)`,
  },
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  leftIcon,
  rightIcon,
  disabled,
  children,
  ...props
}) => {
  const baseStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    fontWeight: typographyFlat.fontWeight.medium,
    borderRadius: borderRadius.md,
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    opacity: disabled || loading ? 0.6 : 1,
    transition: `all ${transitions.base} ease-in-out`,
    width: fullWidth ? '100%' : 'auto',
    ...variantStyles[variant],
    ...sizeStyles[size],
  };

  return (
    <button
      style={baseStyle}
      disabled={disabled || loading}
      aria-busy={loading}
      aria-disabled={disabled || loading}
      {...props}
    >
      {loading && <span aria-label="Loading">⏳</span>}
      {!loading && leftIcon && <span aria-hidden="true">{leftIcon}</span>}
      {children}
      {!loading && rightIcon && <span aria-hidden="true">{rightIcon}</span>}
    </button>
  );
};
